import React, { useState, useRef, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    Stack,
    TextField,
    InputLabel,
    Paper,
    MenuItem,
    CircularProgress,
    RadioGroup,
    Radio,
    FormControlLabel
} from "@mui/material";
import MDInput from "components/MDInput";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDSelect from "components/MDSelect";
import MDDatePicker from "components/MDDatePicker";
import { useUser } from "context/UserContext";
import { useMaterialUIController } from "context";
import MDTypography from "components/MDTypography";
import { Formik, Form, useFormikContext } from "formik";
import { get, post, postNoResponse } from "utils/WebService";
import { errorAlert, successAlert } from "utils/Functions";
import * as Yup from "yup";
import { ESCURO } from "assets/coresTema";

const FormikSync = ({ setFormValues, open }) => {
    const { values, resetForm } = useFormikContext();
    
    useEffect(() => {
        setFormValues(values);
    }, [values, setFormValues]);

    useEffect(() => {
        if (open) {
            resetForm();
        }
    }, [open, resetForm]);

    return null;
};

export default function CreateAgendaModal({ open, onClose, usuarios, fetchAgenda, eventoPai = null }) {

    const [controller] = useMaterialUIController();
    const { darkMode } = controller;

    const [processos, setProcessos] = useState([]);
    const [loadingProcessos, setLoadingProcessos] = useState(false);
    const [searchError, setSearchError] = useState(null);
    const [selectedProcesso, setSelectedProcesso] = useState(null);
    const [formValues, setFormValues] = useState({});
    const [isSelecting, setIsSelecting] = useState(false);
    const [tipoBusca, setTipoBusca] = useState("PROCESSO");

    const inputProcessoRef = useRef(null);
    const debounceRef = useRef(null);

    const { statusPrazoEnum, tipoEventoEnum, sistemasEnum } = useUser();

    const initialValues = {
        processo: "",
        cliente: "",
        parteContraria: "",
        sistema: "",
        data: "",
        dataPublicacao: "",
        tipoPrazo: null,
        statusPrazo: null,
        pessoa: null,
        qtdDiasAvisar: "",
        descricao: "",
    };

    const validationSchema = Yup.object({
        data: Yup.string().required("Data é obrigatória"),
        dataPublicacao: eventoPai ? Yup.string() : Yup.string().required("Data da publicação é obrigatória"),
        tipoPrazo: Yup.object().nullable().required("Tipo de prazo é obrigatório"),
        statusPrazo: Yup.object().nullable().required("Status é obrigatório"),
        pessoa: Yup.object().nullable().required("Pessoa é obrigatória"),
        qtdDiasAvisar: Yup.number()
            .typeError("Deve ser um número")
            .min(0, "Não pode ser negativo")
            .required("Informe a quantidade de dias de aviso"),
    });

    useEffect(() => {
        if (isSelecting) return;

        if (!formValues.processo) {
            setProcessos([]);
            return;
        }

        if (debounceRef.current) clearTimeout(debounceRef.current);

        debounceRef.current = setTimeout(() => {
            searchProcess(formValues.processo);
        }, 500);

        return () => clearTimeout(debounceRef.current);

    }, [formValues.processo]);

    useEffect(() => {
        if (open) {
            setSelectedProcesso(null);
            setProcessos([]);
            setSearchError(null);
            setFormValues(initialValues);
        }
    }, [open]);

    const searchProcess = (search) => {
        if (!search || search.trim() === "") {
            setProcessos([]);
            return;
        }

        setLoadingProcessos(true);
        setSearchError(null);

        if (tipoBusca === "PROCESSO") {
            get(`/processo/list-all?search=${encodeURIComponent(search.replace(/\D/g, ''))}`)
                .then((res) => {
                    setProcessos(res.list || []);
                })
                .catch((err) => {
                    setProcessos([]);
                    setSearchError(err?.message || "Erro ao buscar processos");
                })
                .finally(() => setLoadingProcessos(false));
        } else {
            post("/processo/list-casos", { nomeCaso: search, pagination: { pageIndex: 0, pageSize: 15 }})
                .then(({ data }) => setProcessos(data || []))
                .catch((err) => {
                    setProcessos([]);
                    setSearchError("Erro ao buscar Casos");
                })
                .finally(() => setLoadingProcessos(false));
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="sm"
            PaperProps={{
                sx: {
                    backgroundColor: darkMode ? ESCURO.cartao : "#fff",
                    color: darkMode ? "#fff" : "#000",
                },
            }}
        >
            <DialogTitle>Novo Evento</DialogTitle>

            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={(values, actions) => {

                    const body = {
                        data: values.data,
                        tipoPrazo: values.tipoPrazo?.code,
                        dataPublicacao: values.dataPublicacao,
                        statusPrazo: values.statusPrazo?.code,
                        pessoa: { id: values.pessoa?.id },
                        qtdDiasAvisar: Number(values.qtdDiasAvisar),
                        descricao: values.descricao,
                        cliente: values.cliente,
                        parteContraria: values.parteContraria,
                        sistema: values.sistema,
                        processo: (selectedProcesso?.id || eventoPai?.idCasoProcessual) ? { id: selectedProcesso?.id || eventoPai?.idCasoProcessual } : null,
                        tbSolicitacao: eventoPai?.idSolicitacaoPai ? { id: eventoPai.idSolicitacaoPai } : null
                    };

                    postNoResponse("/prazo/save", body)
                        .then(() => {
                            onClose();
                            successAlert("Agenda criada com sucesso!");
                            fetchAgenda();
                        })
                        .catch(() => errorAlert("Erro ao criar agenda!"))
                        .finally(() => actions.setSubmitting(false));
                }}
            >
                {(formik) => (
                        <Form>
                            <FormikSync setFormValues={setFormValues} open={open} />
                            <DialogContent dividers>

                                <Stack spacing={1} sx={{ mb: 2 }}>
                                    {(!eventoPai?.idCasoProcessual && !selectedProcesso) && (
                                        <Grid item xs={12}>
                                        <MDTypography variant="button" fontWeight="bold">Vincular Processo (Opcional):</MDTypography>
                                        <MDBox display="flex" alignItems="center" gap={2} mb={1} mt={1}>
                                            <RadioGroup
                                                row
                                                value={tipoBusca}
                                                onChange={(e) => {
                                                    setTipoBusca(e.target.value);
                                                    setProcessos([]);
                                                    formik.setFieldValue("processo", "");
                                                    setSelectedProcesso(null);
                                                }}
                                            >
                                                <FormControlLabel value="PROCESSO" control={<Radio />} label="Processo Judicial" />
                                                <FormControlLabel value="CASO" control={<Radio />} label="Caso Simplificado" />
                                            </RadioGroup>
                                        </MDBox>
                                        <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>
                                            {tipoBusca === "PROCESSO" ? "Pesquise por Número" : "Pesquise pelo Nome do Caso"}
                                        </InputLabel>

                                        <div style={{ position: "relative" }}>
                                            <TextField
                                                type="text"
                                                fullWidth
                                                inputRef={inputProcessoRef}
                                                value={formik.values.processo}
                                                onChange={(e) => {
                                                    formik.setFieldValue("processo", e.target.value);
                                                    setSelectedProcesso(null);
                                                }}
                                            />

                                            {(processos.length > 0 || loadingProcessos || searchError) && (
                                                <Paper
                                                    elevation={3}
                                                    style={{
                                                        position: "absolute",
                                                        width: "100%",
                                                        zIndex: 20,
                                                        maxHeight: 250,
                                                        overflowY: "auto",
                                                    }}
                                                >
                                                    {loadingProcessos && (
                                                        <MenuItem>
                                                            <CircularProgress size={20} />
                                                            &nbsp; Buscando...
                                                        </MenuItem>
                                                    )}

                                                    {!loadingProcessos && searchError && (
                                                        <MenuItem disabled sx={{ color: 'error.main' }}>
                                                            ⚠️ {searchError}
                                                        </MenuItem>
                                                    )}

                                                    {!loadingProcessos && !searchError &&
                                                        processos.map((p) => (
                                                            <MenuItem
                                                                key={p.id}
                                                                onClick={() => {
                                                                    setIsSelecting(true);
                                                                    setSelectedProcesso(p);
                                                                    formik.setFieldValue("processo", p.processo || p.nomeCaso);
                                                                    formik.setFieldValue("cliente", p.cliente);
                                                                    formik.setFieldValue("parteContraria", p.parteContraria);
                                                                    formik.setFieldValue("sistema", p.processoEletronico || p.sistema);

                                                                    setProcessos([]);
                                                                    setLoadingProcessos(false);

                                                                    inputProcessoRef.current?.blur();
                                                                    setTimeout(() => setIsSelecting(false), 300);
                                                                }}
                                                            >
                                                                <b>{p.processo || p.nomeCaso}</b> {p.cliente ? `— ${p.cliente}` : ''}
                                                            </MenuItem>
                                                        ))}
                                                </Paper>
                                            )}
                                        </div>
                                    </Grid>
                                    )}

                                    {(!eventoPai?.idCasoProcessual && selectedProcesso) && (
                                        <MDTypography variant="body2">
                                            <b>Cliente:</b> {formik.values.cliente || "—"} &nbsp;|&nbsp;
                                            <b>Parte Contrária:</b> {formik.values.parteContraria || "—"} &nbsp;|&nbsp;
                                            <b>Sistema:</b> {sistemasEnum.find(t => t.code === formik.values.sistema)?.name || "-"}
                                        </MDTypography>
                                    )}
                                </Stack>

                                <fieldset style={{ border: "none" }}>
                                    <Grid container spacing={2}>

                                        <Grid item xs={12}>
                                            <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Descrição</InputLabel>
                                            <TextField
                                                multiline
                                                minRows={3}
                                                fullWidth
                                                value={formik.values.descricao}
                                                onChange={(e) =>
                                                    formik.setFieldValue("descricao", e.target.value)
                                                }
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Data Final do Prazo</InputLabel>
                                            <MDDatePicker
                                                value={formik.values.data}
                                                onChange={(dates, dateStr) => formik.setFieldValue("data", dateStr)}
                                                options={{
                                                    enableTime: true,
                                                    dateFormat: "Y-m-d\\TH:i",
                                                    minuteIncrement: 15,
                                                    time_24hr: true,
                                                    disableMobile: true,
                                                    onOpen: function(selectedDates, dateStr, instance) {
                                                        if (instance && instance.calendarContainer) {
                                                            instance.calendarContainer.style.transform = 'scale(0.85)';
                                                            instance.calendarContainer.style.transformOrigin = 'top center';
                                                        }
                                                    }
                                                }}
                                                input={{
                                                    fullWidth: true,
                                                    InputLabelProps: { shrink: true }
                                                }}
                                            />
                                            {formik.errors.data && formik.touched.data && (
                                                <MDTypography variant="caption" color="error">
                                                    {formik.errors.data}
                                                </MDTypography>
                                            )}
                                        </Grid>

                                        {!eventoPai && (
                                            <Grid item xs={12} sm={6}>
                                                <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Data da Publicação</InputLabel>
                                                <MDDatePicker
                                                    value={formik.values.dataPublicacao}
                                                    onChange={(dates, dateStr) => formik.setFieldValue("dataPublicacao", dateStr)}
                                                    options={{
                                                        enableTime: false,
                                                        dateFormat: "Y-m-d",
                                                        disableMobile: true,
                                                        onOpen: function(selectedDates, dateStr, instance) {
                                                            if (instance && instance.calendarContainer) {
                                                                instance.calendarContainer.style.transform = 'scale(0.85)';
                                                                instance.calendarContainer.style.transformOrigin = 'top center';
                                                            }
                                                        }
                                                    }}
                                                    input={{
                                                        fullWidth: true,
                                                        InputLabelProps: { shrink: true }
                                                    }}
                                                />
                                                {formik.errors.dataPublicacao &&
                                                    formik.touched.dataPublicacao && (
                                                        <MDTypography variant="caption" color="error">
                                                            {formik.errors.dataPublicacao}
                                                        </MDTypography>
                                                    )}
                                            </Grid>
                                        )}

                                        <Grid item xs={12} sm={6}>
                                            <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Responsável</InputLabel>
                                            <MDSelect
                                                options={usuarios}
                                                filterOption={() => true}
                                                placeholder="Selecione"
                                                getOptionLabel={(o) => o.nome}
                                                getOptionValue={(o) => o.id}
                                                onChange={(opt) => formik.setFieldValue("pessoa", opt)}
                                            />
                                            {formik.errors.pessoa && formik.touched.pessoa && (
                                                <MDTypography variant="caption" color="error">
                                                    {formik.errors.pessoa}
                                                </MDTypography>
                                            )}
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Qtd dias antes para avisar</InputLabel>
                                            <MDInput
                                                type="number"
                                                fullWidth
                                                value={formik.values.qtdDiasAvisar}
                                                onChange={(e) =>
                                                    formik.setFieldValue("qtdDiasAvisar", e.target.value)
                                                }
                                            />
                                            {formik.errors.qtdDiasAvisar &&
                                                formik.touched.qtdDiasAvisar && (
                                                    <MDTypography variant="caption" color="error">
                                                        {formik.errors.qtdDiasAvisar}
                                                    </MDTypography>
                                                )}
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Tipo do Evento</InputLabel>
                                            <MDSelect
                                                options={tipoEventoEnum}
                                                placeholder="Selecione"
                                                getOptionLabel={(o) => o.name}
                                                getOptionValue={(o) => o.code}
                                                onChange={(opt) => formik.setFieldValue("tipoPrazo", opt)}
                                            />
                                            {formik.errors.tipoPrazo &&
                                                formik.touched.tipoPrazo && (
                                                    <MDTypography variant="caption" color="error">
                                                        {formik.errors.tipoPrazo}
                                                    </MDTypography>
                                                )}
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Status</InputLabel>
                                            <MDSelect
                                                options={statusPrazoEnum}
                                                placeholder="Selecione"
                                                getOptionValue={(o) => o.code}
                                                getOptionLabel={(o) => o.name}
                                                onChange={(opt) => formik.setFieldValue("statusPrazo", opt)}
                                            />
                                            {formik.errors.statusPrazo &&
                                                formik.touched.statusPrazo && (
                                                    <MDTypography variant="caption" color="error">
                                                        {formik.errors.statusPrazo}
                                                    </MDTypography>
                                                )}
                                        </Grid>

                                    </Grid>
                                </fieldset>

                            </DialogContent>

                            <DialogActions>
                                <MDButton
                                    variant="contained"
                                    color="info"
                                    onClick={onClose}
                                    disabled={formik.isSubmitting}
                                >
                                    Cancelar
                                </MDButton>

                                <MDButton
                                    type="submit"
                                    variant="contained"
                                    color="info"
                                    disabled={formik.isSubmitting}
                                    startIcon={
                                        formik.isSubmitting ? (
                                            <CircularProgress size={16} color="inherit" />
                                        ) : null
                                    }
                                >
                                    {formik.isSubmitting ? "Salvando..." : "Salvar"}
                                </MDButton>

                            </DialogActions>
                        </Form>
                    )
                }
            </Formik>
        </Dialog>
    );
}

